import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationMap.css';

// Fix default marker icon issue with webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Helper component to dynamically update map bounds and view
function MapUpdater({ clusters }) {
    const map = useMap();

    useEffect(() => {
        if (clusters.length === 0) return;

        if (clusters.length === 1) {
            map.setView([clusters[0].latitude, clusters[0].longitude], 15);
        } else {
            const lats = clusters.map(c => c.latitude);
            const lons = clusters.map(c => c.longitude);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLon = Math.min(...lons);
            const maxLon = Math.max(...lons);

            map.fitBounds([
                [minLat, minLon],
                [maxLat, maxLon]
            ], { padding: [50, 50] });
        }
    }, [clusters, map]);

    return null;
}

// Group markers within ~20 meters (0.0002 degrees)
function getClusters(activeMarkers) {
    const clusters = [];
    const threshold = 0.0002;

    activeMarkers.forEach(marker => {
        const cluster = clusters.find(c => {
            const latDiff = Math.abs(c.latitude - marker.latitude);
            const lonDiff = Math.abs(c.longitude - marker.longitude);
            return latDiff < threshold && lonDiff < threshold;
        });

        if (cluster) {
            cluster.devices.push(marker);
        } else {
            clusters.push({
                latitude: marker.latitude,
                longitude: marker.longitude,
                devices: [marker]
            });
        }
    });

    return clusters;
}

/**
 * LocationMap — Renders a device location on OpenStreetMap via Leaflet
 *
 * Props:
 *   latitude   — GPS latitude
 *   longitude  — GPS longitude
 *   label      — optional popup label (e.g., vehicle name)
 *   timestamp  — optional last-seen timestamp
 *   markers    — array of device marker objects [{ latitude, longitude, label, cabinets, timestamp }]
 */
function LocationMap({ latitude, longitude, label, timestamp, markers }) {
    let activeMarkers = [];
    if (markers && markers.length > 0) {
        activeMarkers = markers.filter(m => m.latitude != null && m.longitude != null && !(m.latitude === 0 && m.longitude === 0));
    } else if (latitude != null && longitude != null && !(latitude === 0 && longitude === 0)) {
        activeMarkers = [{ latitude, longitude, label, timestamp }];
    }

    if (activeMarkers.length === 0) {
        return (
            <div className="map-no-data">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                </svg>
                <span>Location data unavailable</span>
            </div>
        );
    }

    const clusters = getClusters(activeMarkers);
    const centerPosition = [clusters[0].latitude, clusters[0].longitude];

    return (
        <div className="map-wrapper">
            <MapContainer
                center={centerPosition}
                zoom={12}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%', borderRadius: '10px' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapUpdater clusters={clusters} />
                {clusters.map((cluster, idx) => {
                    if (cluster.devices.length === 1) {
                        const m = cluster.devices[0];
                        return (
                            <Marker key={idx} position={[m.latitude, m.longitude]}>
                                <Popup>
                                    <strong>{m.label || 'Device Location'}</strong>
                                    <br />
                                    {m.latitude.toFixed(6)}, {m.longitude.toFixed(6)}
                                    {m.cabinets && m.cabinets.length > 0 && (
                                        <>
                                            <br />
                                            <strong>Cabinets:</strong> {m.cabinets.join(', ')}
                                        </>
                                    )}
                                    {m.timestamp && (
                                        <>
                                            <br />
                                            <small>{new Date(m.timestamp).toLocaleString()}</small>
                                        </>
                                    )}
                                </Popup>
                            </Marker>
                        );
                    } else {
                        const clusterIcon = L.divIcon({
                            html: `<div class="map-cluster-badge"><span>${cluster.devices.length}</span></div>`,
                            className: 'map-cluster-icon',
                            iconSize: [30, 30],
                            iconAnchor: [15, 15]
                        });

                        return (
                            <Marker key={idx} position={[cluster.latitude, cluster.longitude]} icon={clusterIcon}>
                                <Popup>
                                    <div className="map-cluster-popup">
                                        <strong>Multiple Devices ({cluster.devices.length})</strong>
                                        <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                                            {cluster.devices.map((dev, dIdx) => (
                                                <div key={dIdx} style={{ 
                                                    marginBottom: '0.4rem', 
                                                    borderBottom: dIdx < cluster.devices.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none', 
                                                    paddingBottom: '0.3rem' 
                                                }}>
                                                    <strong>{dev.label}</strong>
                                                    {dev.cabinets && dev.cabinets.length > 0 && (
                                                        <div><small>Cabinets: {dev.cabinets.join(', ')}</small></div>
                                                    )}
                                                    {dev.timestamp && (
                                                        <div><small style={{ color: '#8892b0' }}>{new Date(dev.timestamp).toLocaleTimeString()}</small></div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    }
                })}
            </MapContainer>
        </div>
    );
}

export default LocationMap;
